import React from 'react';
import { ToolContainer } from './ToolContainer';
import type { ToolId } from '../utils/slugs';
import { InfoIcon } from '../Icons';
import { CakePricingCalculatorClient } from './ui/CakePricingCalculatorClient';

interface CakePricingCalculatorProps {
    setActiveTool?: (toolId: ToolId) => void;
    breadcrumbs: React.ReactNode;
}

export const CakePricingCalculator: React.FC<CakePricingCalculatorProps> = ({ setActiveTool, breadcrumbs }) => {
    return (
        <ToolContainer
            title="Free Cake Pricing Calculator & Bakery Cost App"
            description="Calculate the perfect price for your custom cakes and baked goods. Ideal for pricing wedding cakes, birthday cakes, cupcakes, and more. Factor in ingredients for chocolate, vanilla, or red velvet cake, plus labor and cake delivery costs."
            setActiveTool={setActiveTool || (() => { })}
            breadcrumbs={breadcrumbs}
            toolId="cake-pricing"
        >
            <CakePricingCalculatorClient />
            <CakePricingCalculatorContent />
        </ToolContainer>
    );
};

const CakePricingCalculatorContent: React.FC = () => (
    <div className="mt-12 space-y-12 pt-8 border-t border-brand-border">
        <div className="prose prose-pink max-w-none">
            <h2 className="font-serif text-3xl font-bold text-brand-text-title mb-4">The Ultimate Cake Pricing Calculator for Bakers</h2>
            <p className="text-lg text-brand-text-body/80 leading-relaxed">
                Turning your baking passion into a business? The biggest challenge is ensuring you're actually making a profit. Our free <strong>Cake Pricing Calculator</strong> takes the guesswork out of billing. Whether you are selling <strong>custom birthday cakes</strong>, <strong>wedding cakes</strong>, or <strong>cupcakes</strong> by the dozen, this tool accounts for everything—total ingredient costs, your valuable time (labor), overhead expenses, and even delivery.
            </p>
            <p className="text-lg text-brand-text-body/80 leading-relaxed mt-4">
                Designed for home bakers and small business owners, this <strong>Bakery Cost App</strong> ensures you never undercharge for your hard work again. 
                Want to spend less time on spreadsheets? <a href="/saas" className="text-brand-pink font-bold hover:underline">Automate pricing with Bakery Pro</a>.
            </p>
            <div className="mt-6 flex flex-wrap gap-4">
                <a href="/tools/recipe-scaler" className="inline-flex items-center gap-2 px-4 py-2 bg-brand-pink/10 text-brand-pink rounded-lg font-bold hover:bg-brand-pink hover:text-white transition-all">
                    <span>⚖️</span> Scale your recipe next
                </a>
            </div>
        </div>

        <div className="bg-gradient-to-br from-brand-pink/5 to-white p-6 rounded-2xl border border-brand-pink/10 shadow-sm">
            <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Recommended Gear for Your Business</h3>
            <div className="grid sm:grid-cols-2 gap-4">
                <a href="https://amzn.to/3PqXy8C" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl border border-brand-border hover:border-brand-pink hover:shadow-md transition-all group">
                    <span className="text-3xl">📦</span>
                    <div>
                        <p className="font-bold text-brand-text-title group-hover:text-brand-pink transition-colors">Cake Box Starter Kit</p>
                        <p className="text-xs text-brand-text-body/70">Professional packaging adds value to your cakes.</p>
                    </div>
                </a>
                <a href="https://amzn.to/3PqXy8C" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-4 bg-white rounded-xl border border-brand-border hover:border-brand-pink hover:shadow-md transition-all group">
                    <span className="text-3xl">📝</span>
                    <div>
                        <p className="font-bold text-brand-text-title group-hover:text-brand-pink transition-colors">Order Order Pad</p>
                        <p className="text-xs text-brand-text-body/70">Keep track of every custom order detail.</p>
                    </div>
                </a>
            </div>
            <p className="text-[10px] text-brand-text-body/40 mt-3 text-center">
                *As an Amazon Associate, we earn from qualifying purchases. This helps keep our tools free!
            </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title flex items-center gap-2">
                    <InfoIcon className="w-6 h-6 text-brand-pink" />
                    How to Price a Cake Correctly
                </h3>
                <p className="text-sm text-brand-text-body/80 mb-4">
                    New to pricing? Read our <a href="/cake-pricing-guide" className="text-brand-pink font-bold hover:underline">Ultimate Guide to Cake Pricing</a> for a deep dive.
                </p>
                <ol className="space-y-3 text-sm text-brand-text-body/80 list-decimal pl-4 marker:text-brand-pink marker:font-bold">
                    <li><strong>Start with Ingredients:</strong> List every item, including packaging (cake boards, boxes). This is your base cost.</li>
                    <li><strong>Add Your Labor:</strong> Don't work for free! Charge a fair hourly rate for shopping, baking, decorating, and cleaning.</li>
                    <li><strong>Factor Overhead:</strong> Cover the "hidden" costs like electricity, water, insurance, and equipment wear (usually 10-20% is safe).</li>
                    <li><strong>Set Profit Margin:</strong> This is for business growth. A 30-50% margin is standard for sustainable bakeries.</li>
                    <li><strong>Delivery & Tax:</strong> Add mileage rates for delivery and ensure you calculate sales tax if local laws require it.</li>
                </ol>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-brand-border shadow-sm">
                <h3 className="font-serif font-bold text-xl mb-4 text-brand-text-title">Frequently Asked Questions</h3>
                <div className="space-y-4">
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            What is a good profit margin for cakes?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            For custom cakes and baked goods, a profit margin of <strong>30% to 50%</strong> is recommended. This profit allows you to reinvest in your business (new mixers, branding, marketing) and protects you from unexpected costs.
                        </p>
                    </details>
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            How do I calculate overhead for a home bakery?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Since you can't easily meter your oven's specific usage, a simple method is to add a percentage (usually <strong>10-20%</strong>) to your direct costs (Ingredients + Labor). This creates a buffer that covers utilities and wear-and-tear.
                        </p>
                    </details>
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            Should I charge for cake tasting boxes?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Yes! Tasting boxes require ingredients, time, and packaging. Many professionals charge a consultation fee (e.g., $30-$50) which can be deducted from the final wedding cake order if the client books with you.
                        </p>
                    </details>
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            How do I price specialized diets (Vegan/GF)?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Niche items like <strong>Gluten-Free Cakes</strong>, <strong>Vegan Cakes</strong>, or <strong>Keto Desserts</strong> use specialty ingredients (almond flour, sweeteners) that cost more. Always use a calculator to track these higher costs and price accordingly.
                        </p>
                    </details>
                    <details className="group">
                        <summary className="flex justify-between items-center font-bold text-brand-text-title cursor-pointer hover:text-brand-pink transition-colors">
                            How much should I charge for cake delivery?
                            <span className="text-brand-pink group-open:rotate-180 transition-transform">▼</span>
                        </summary>
                        <p className="text-sm text-brand-text-body/80 mt-2 leading-relaxed">
                            Charge a base fee for your time plus a per-mile rate (IRS standard is ~67 cents/mile). For <strong>Wedding Cake Delivery</strong>, which involves setup and high stress, many bakers charge a premium hourly rate for the travel time.
                        </p>
                    </details>
                </div>
            </div>
        </div>
    </div>
);
